<script setup lang="ts">
import { useConfigStore } from '@gms/stores/src/stores/config';
import { useThemeStore } from '@gms/stores/src/stores/theme';
import { AllThemes } from '../../../vuetify2-themes/dist';

const themeStore = useThemeStore();
const configStore = useConfigStore();

/** The client the local and the staging API know; see libs/app-config. */
const STAGING_CLIENT_ID = '77cdcf98-c9ca-464d-a706-cf863134b639';
</script>

<template>
  <div class="main-panel">
    <section>
      <h3>API</h3>
      <p>Configure the api connection</p>
      <div class="list">
        <div class="list-item" style="display: flex; flex-direction: row;">
          <div class="input-container">
            <label>API Url</label>
            <p class="description">Choose the url of the api</p>
            <!-- data-bwignore is bitwarden's ignore attribute -->
            <input
              type="text"
              name="API Url"
              autocomplete="off"
              data-bwignore="true"
              v-model="configStore.config?.api.baseUrl" 
            />
          </div>
          <div style="margin: 46px 0 -6px 12px;">
            <button
              @click="configStore.config.api.baseUrl = 'http://localhost:8081'"
              type="reset"
              style="height: 87%; margin-right: 12px"
            >
              Local
            </button>
            <button
              @click="configStore.config.api.baseUrl = 'https://api.staging.book-n-use.com/v4'"
              type="reset"
              class="mx-2"
              style="height: 87%"
            >
              Staging
            </button>
          </div>
        </div>
        <div class="list-item" style="display: flex; flex-direction: row;">
          <div class="input-container">
            <label>Client ID</label>
            <p class="description">Choose the client id</p>
            <!-- data-bwignore is bitwarden's ignore attribute -->
            <input
              type="text"
              name="Client ID"
              autocomplete="off"
              data-bwignore="true"
              v-model="configStore.config?.api.clientId"
            />
            <v-fade-transition>
              <!--
                A client the API does not know gets responses with only minimal fields:
                nothing fails, flows just stall (the booking review waits forever for a
                reservation summary it was never told about).
              -->
              <div
                v-if="configStore.config?.api.clientId !== STAGING_CLIENT_ID"
                class="warning-hint"
                role="status"
              >
                <strong>Not the staging client ID.</strong>
                The local and staging APIs answer an unknown client with minimal fields only,
                so flows like the booking can stall without any error.
              </div>
            </v-fade-transition>
          </div>
          <v-fade-transition>
            <div
              v-if="configStore.config?.api.clientId !== STAGING_CLIENT_ID"
              style="margin: 46px 0 0 12px; align-self: flex-start;"
            >
              <!-- A fixed height: the hint below the input makes the row taller. -->
              <button
                @click="configStore.config.api.clientId = STAGING_CLIENT_ID"
                type="reset"
                style="height: 38px"
              >
                Staging default
              </button>
            </div>
          </v-fade-transition>
        </div>
      </div>
    </section>
    <section>
      <h3>Theme</h3>
      <p>Configure the current theme</p>
      <div class="list">
        <div class="list-item">
          <div class="input-container">
            <label>Theme</label>
            <p class="description">Choose the general theme of the page</p>
            <select
              v-model="themeStore.theme"
              autocomplete="off"
              data-bwignore="true"
            >
              <option v-for="theme in AllThemes" :key="`theme-${theme.name}`" :value="theme">
                {{ theme.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="list-item">
          <label class="checkbox-container">
            <input
              type="checkbox"
              class="checkbox"
              autocomplete="off"
              data-bwignore="true"
              v-model="themeStore.dark"
            />
            <div class="checkbox-label-container">
              <span class="checkbox-label">Dark</span>
              <span class="checkbox-description">Use the dark variant of the theme</span>
            </div>
          </label>
        </div>
      </div>
    </section>
    <section>
      <h3>General Information</h3>
      <p>General information about the current configuration</p>
      <div class="list">
        <div class="list-item">
          <div class="input-container">
            <label>Version</label>
            <p class="description">The current application version</p>
            <span>{{ configStore.fullVersion }}</span>
          </div>
        </div>
        <div class="list-item">
          <div class="input-container">
            <label>TU Darmstadt SSO Url</label>
            <p class="description">URL to the single sign on server of TU Darmstadt</p>
            <span>{{ configStore.config?.tuDarmstadtCasUrl }}</span>
          </div>
        </div>
        <div class="list-item">
          <div class="input-container">
            <label>PayPal Client ID</label>
            <p class="description">Client ID used by the PayPal client for identification</p>
            <span style="word-break: break-all;">{{ configStore.config?.payPalClientId }}</span>
          </div>
        </div>
        <div class="list-item">
          <div class="input-container">
            <label>PayPal Environment</label>
            <p class="description">Determines the backing URL for PayPal (sandbox or live system?)</p>
            <span style="word-break: break-all;">{{ configStore.config?.payPalEnvironment }}</span>
          </div>
        </div>
        <div class="list-item">
          <div class="input-container">
            <label>Redirect URL</label>
            <p class="description">
              Used to redirect users of the wrong role to the correct website
            </p>
            <span>{{ configStore.config?.redirectUrl }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="css" scoped>
.main-panel {
  padding: calc(var(--tsrd-font-size) * 0.75);
  background: #f9fafb;
  overflow-y: auto;
  height: 100%;
}

section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #fff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  box-shadow: none;
}

h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #101828;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
}

p {
  color: #667085;
  font-size: 0.8rem;
  margin: 0 0 1rem 0;
  line-height: 1.4;
  text-align: left;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.list-item {
  width: 100%;
}

.input-container {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #101828;
  text-align: left;
}

button {
  padding: 9px 16px 12px 16px;
  border: 1px solid #eaecf0;
  border-radius: 0.375rem;
  background-color: #f9fafb;
  color: #101828;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

.description {
  font-size: 0.8rem;
  color: #667085;
  margin: 0;
  line-height: 1.3;
  text-align: left;
}

.warning-hint {
  margin-top: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #fedf89;
  border-left-width: 4px;
  border-radius: 0.375rem;
  background-color: #fffaeb;
  color: #93370d;
  font-size: 0.8rem;
  line-height: 1.4;
  text-align: left;
}

input[type='text'] {
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background-color: #f9fafb;
  color: #101828;
  border: 1px solid #eaecf0;
  font-size: 0.875rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  transition: all 0.15s ease;
}

input:hover {
  border-color: #d0d5dd;
}

input:focus {
  outline: none;
  border-color: #98a2b3;
  box-shadow: 0 0 0 3px #eaecf0;
}

select {
  appearance: none;
  width: 100%;
  padding: 0.5rem 3rem 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background-color: #f9fafb;
  color: #101828;
  border: 1px solid #eaecf0;
  font-size: 0.875rem;
  transition: all 0.15s ease;
  cursor: pointer;
  background-image: url(data:image/svg+xml;utf8:,<svg fill='%236b7280' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>);
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25rem;
}

select:hover {
  border-color: #d0d5dd;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  padding: 0.375rem;
  border-radius: 0.375rem;
  transition: background-color 0.15s ease;
}

.checkbox {
  appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #d0d5dd;
  border-radius: 0.25rem;
  background-color: #f9fafb;
  display: grid;
  place-items: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.checkbox:hover {
  border-color: #98a2b3;
}

.checkbox:checked {
  background-color: #101828;
  border-color: #101828;
}

.checkbox-label-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.checkbox-label {
  color: #101828;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
}

.checkbox-description {
  color: #667085;
  font-size: 0.8rem;
  line-height: 1.3;
  text-align: left;
}

.checkbox::after {
  content: '';
  width: 0.4rem;
  height: 0.6rem;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-top: -3px;
}
</style>